import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, Smile, Paperclip, Mic, MicOff, Minimize2, Radio, Video, VideoOff, Maximize2, PhoneOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, WS_URL, AuthContext } from "../App";
import EmojiPicker from "./EmojiPicker";
import UserActionMenu from "./UserActionMenu";
import ImageLightbox from "./ImageLightbox";
import IMVideoCallWindow from "./IMVideoCallWindow";
import { playDingDingSound } from "../utils/sounds";
import usePwaInstall from "../hooks/usePwaInstall";
import { requestMedia, toastMediaError } from "../utils/media";

const PrivateMessageWindow = ({ targetUserId, targetUsername, onClose, style }) => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { installed, canInstall, isIos, promptInstall } = usePwaInstall();
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState({ x: style?.left || 100, y: style?.top || 100 });
  const [size, setSize] = useState({ width: 280, height: 320 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showUserMenu, setShowUserMenu] = useState(false);
  // On phones the window is a fixed full-screen modal (see .pm-window mobile CSS), so
  // dragging/resizing is disabled — this prevents the window from getting lost off-screen.
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  
  // Voice chat state
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPushToTalk, setIsPushToTalk] = useState(false);
  const [isPTTActive, setIsPTTActive] = useState(false);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [incomingCall, setIncomingCall] = useState(false);
  const localStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  
  // Webcam state
  const [showWebcam, setShowWebcam] = useState(false);
  const [isWebcamEnabled, setIsWebcamEnabled] = useState(false);
  const [webcamConnected, setWebcamConnected] = useState(false);
  const [localVideoStream, setLocalVideoStream] = useState(null);
  const [remoteVideoStream, setRemoteVideoStream] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);  // For camera preview/test
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const videoPeerConnectionRef = useRef(null);
  
  const wsRef = useRef(null);
  const pendingMessagesRef = useRef([]); // messages typed before the socket finished (re)connecting
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const windowRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  
  // Refs for voice handlers to avoid useEffect re-runs
  const handleVoiceOfferRef = useRef(null);
  const handleVoiceAnswerRef = useRef(null);
  const handleVoiceIceCandidateRef = useRef(null);
  const stopVoiceRef = useRef(null);
  
  // Refs for webcam handlers
  const handleWebcamOfferRef = useRef(null);
  const handleWebcamAnswerRef = useRef(null);
  const handleWebcamIceCandidateRef = useRef(null);
  const stopWebcamRef = useRef(null);
  
  // Refs to track if we've shown end-call notifications (prevent duplicates)
  const voiceEndNotifiedRef = useRef(false);
  const webcamEndNotifiedRef = useRef(false);

  // WebRTC configuration
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch existing messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(`${API}/messages/private/${targetUserId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessages(response.data);
      } catch (error) {
        console.error("Failed to fetch messages:", error);
      }
    };
    fetchMessages();
  }, [targetUserId, token]);

  // Create peer connection for voice
  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection(rtcConfig);
    
    pc.ontrack = (event) => {
      console.log("Received remote audio");
      const audio = new Audio();
      audio.srcObject = event.streams[0];
      audio.autoplay = true;
      audio.id = `pm-audio-${targetUserId}`;
      const existing = document.getElementById(`pm-audio-${targetUserId}`);
      if (existing) existing.remove();
      document.body.appendChild(audio);
    };
    
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "pm_voice_ice_candidate",
          target_user_id: targetUserId,
          candidate: event.candidate
        }));
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log("PM Voice ICE state:", pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected') {
        setVoiceConnected(true);
        toast.success("Voice connected!");
      } else if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        setVoiceConnected(false);
      }
    };
    
    peerConnectionRef.current = pc;
    return pc;
  }, [targetUserId, rtcConfig]);

  // Handle incoming voice offer
  const handleVoiceOffer = useCallback(async (offer) => {
    setIncomingCall(true);
    
    // Auto-answer for now (could add accept/reject UI)
    try {
      const stream = await requestMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      localStreamRef.current = stream;
      
      const pc = createPeerConnection();
      stream.getAudioTracks().forEach(track => pc.addTrack(track, stream));
      
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "pm_voice_answer",
          target_user_id: targetUserId,
          answer: answer
        }));
      }
      
      setIsVoiceEnabled(true);
      setIncomingCall(false);
    } catch (error) {
      console.error("Failed to answer call:", error);
      toastMediaError(error);
      setIncomingCall(false);
    }
  }, [targetUserId, createPeerConnection]);

  // Handle incoming voice answer
  const handleVoiceAnswer = useCallback(async (answer) => {
    if (peerConnectionRef.current) {
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }, []);

  // Handle incoming ICE candidate
  const handleVoiceIceCandidate = useCallback(async (candidate) => {
    if (peerConnectionRef.current) {
      await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }, []);

  // Keep refs updated
  useEffect(() => {
    handleVoiceOfferRef.current = handleVoiceOffer;
    handleVoiceAnswerRef.current = handleVoiceAnswer;
    handleVoiceIceCandidateRef.current = handleVoiceIceCandidate;
  }, [handleVoiceOffer, handleVoiceAnswer, handleVoiceIceCandidate]);

  // WebSocket connection for private messages with reconnection
  useEffect(() => {
    let ws = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimeout = null;
    let isClosing = false;

    const connect = () => {
      if (isClosing) return;
      
      ws = new WebSocket(`${WS_URL}/ws/private?token=${token}`);
      
      ws.onopen = () => {
        console.log("Connected to private messaging");
        reconnectAttempts = 0; // Reset on successful connection
        // Flush any messages the user sent while the socket was (re)connecting.
        if (pendingMessagesRef.current.length > 0) {
          pendingMessagesRef.current.forEach((content) => {
            try {
              ws.send(JSON.stringify({
                type: "private_message",
                recipient_id: targetUserId,
                content,
                message_type: "text"
              }));
            } catch (e) { /* ignore */ }
          });
          pendingMessagesRef.current = [];
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "private_message" && data.message?.sender_id === targetUserId) {
            setMessages(prev => [...prev, data.message]);
            // Play DING DING sound when receiving a PM
            playDingDingSound();
          } else if (data.type === "private_message_sent" && data.message?.recipient_id === targetUserId) {
            setMessages(prev => [...prev, data.message]);
          } else if (data.type === "typing" && data.user_id === targetUserId) {
            setIsTyping(data.is_typing);
          } else if (data.type === "pm_voice_offer" && data.from_user_id === targetUserId) {
            handleVoiceOfferRef.current?.(data.offer);
          } else if (data.type === "pm_voice_answer" && data.from_user_id === targetUserId) {
            handleVoiceAnswerRef.current?.(data.answer);
          } else if (data.type === "pm_voice_ice_candidate" && data.from_user_id === targetUserId) {
            handleVoiceIceCandidateRef.current?.(data.candidate);
          } else if (data.type === "pm_voice_end" && data.from_user_id === targetUserId) {
            // Only show notification once
            if (!voiceEndNotifiedRef.current) {
              voiceEndNotifiedRef.current = true;
              stopVoiceRef.current?.();
              toast.info("Voice call ended");
              // Reset after a short delay
              setTimeout(() => { voiceEndNotifiedRef.current = false; }, 1000);
            }
          } else if (data.type === "pm_webcam_offer" && data.from_user_id === targetUserId) {
            handleWebcamOfferRef.current?.(data.offer);
          } else if (data.type === "pm_webcam_answer" && data.from_user_id === targetUserId) {
            handleWebcamAnswerRef.current?.(data.answer);
          } else if (data.type === "pm_webcam_ice_candidate" && data.from_user_id === targetUserId) {
            handleWebcamIceCandidateRef.current?.(data.candidate);
          } else if (data.type === "pm_webcam_end" && data.from_user_id === targetUserId) {
            // Only show notification once
            if (!webcamEndNotifiedRef.current) {
              webcamEndNotifiedRef.current = true;
              stopWebcamRef.current?.();
              toast.info("Video call ended");
              // Reset after a short delay
              setTimeout(() => { webcamEndNotifiedRef.current = false; }, 1000);
            }
          } else if (data.type === "pm_blocked") {
            // Recipient has IMs or file uploads disabled
            toast.error(data.message);
          }
        } catch (err) {
          console.error("Error processing PM WebSocket message:", err);
        }
      };

      ws.onerror = (error) => {
        console.error("PM WebSocket error:", error);
      };

      ws.onclose = () => {
        console.log("Disconnected from private messaging");
        if (!isClosing && reconnectAttempts < maxReconnectAttempts) {
          reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000); // Exponential backoff, max 10s
          console.log(`Reconnecting PM WebSocket in ${delay}ms (attempt ${reconnectAttempts})`);
          reconnectTimeout = setTimeout(connect, delay);
        }
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      isClosing = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [token, targetUserId]);

  // Voice functions - Start a voice call
  const startVoice = async () => {
    try {
      const stream = await requestMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      localStreamRef.current = stream;
      
      const pc = createPeerConnection();
      stream.getAudioTracks().forEach(track => pc.addTrack(track, stream));
      
      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "pm_voice_offer",
          target_user_id: targetUserId,
          offer: offer
        }));
      }
      
      setIsVoiceEnabled(true);
      toast.success("Calling... waiting for answer");
    } catch (error) {
      console.error("Failed to start voice:", error);
      toastMediaError(error);
    }
  };

  // Voice functions - Stop voice call with guard against multiple calls
  const isStoppingVoiceRef = useRef(false);
  
  const stopVoice = useCallback(() => {
    // Prevent multiple calls
    if (isStoppingVoiceRef.current) return;
    isStoppingVoiceRef.current = true;
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Remove audio element
    const audioEl = document.getElementById(`pm-audio-${targetUserId}`);
    if (audioEl) audioEl.remove();
    
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    setIsVoiceEnabled(false);
    setVoiceConnected(false);
    setIsMuted(false);
    
    // Reset the stopping flag after a short delay
    setTimeout(() => {
      isStoppingVoiceRef.current = false;
    }, 100);
  }, [targetUserId]);

  // Update stopVoice ref
  useEffect(() => {
    stopVoiceRef.current = stopVoice;
  }, [stopVoice]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopVoiceRef.current?.();
    };
  }, []);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // Push-to-talk handlers
  const handlePTTPress = useCallback(() => {
    if (isPushToTalk && localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = true;
        setIsPTTActive(true);
      }
    }
  }, [isPushToTalk]);

  const handlePTTRelease = useCallback(() => {
    if (isPushToTalk && localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = false;
        setIsPTTActive(false);
      }
    }
  }, [isPushToTalk]);

  const togglePushToTalk = () => {
    if (!isVoiceEnabled) {
      toast.error("Start voice call first");
      return;
    }
    setIsPushToTalk(!isPushToTalk);
    if (!isPushToTalk) {
      if (localStreamRef.current) {
        const audioTrack = localStreamRef.current.getAudioTracks()[0];
        if (audioTrack) audioTrack.enabled = false;
      }
      setIsMuted(true);
      toast.info("Push-to-talk enabled");
    }
  };

  // ==================== WEBCAM FUNCTIONS ====================
  // Create webcam peer connection
  const createWebcamPeerConnection = useCallback(() => {
    if (videoPeerConnectionRef.current) {
      videoPeerConnectionRef.current.close();
    }

    const pc = new RTCPeerConnection(rtcConfig);
    videoPeerConnectionRef.current = pc;

    pc.ontrack = (event) => {
      console.log("Received remote video track");
      setRemoteVideoStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
      setWebcamConnected(true);
      toast.success("Video connected!");
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "pm_webcam_ice_candidate",
          target_user_id: targetUserId,
          candidate: event.candidate
        }));
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("Webcam ICE state:", pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected') {
        setWebcamConnected(true);
      } else if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        setWebcamConnected(false);
      }
    };

    return pc;
  }, [targetUserId, rtcConfig]);

  // Handle incoming webcam offer
  const handleWebcamOffer = useCallback(async (offer) => {
    console.log("Received webcam offer");
    setShowWebcam(true);
    
    try {
      const stream = await requestMedia({ 
        video: { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true
      });
      setLocalVideoStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      const pc = createWebcamPeerConnection();
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "pm_webcam_answer",
          target_user_id: targetUserId,
          answer: answer
        }));
      }
      
      setIsWebcamEnabled(true);
    } catch (error) {
      console.error("Failed to answer video call:", error);
      // Reset the video window so a denied incoming call doesn't leave an empty window open
      setShowWebcam(false);
      setIsWebcamEnabled(false);
      if (localVideoStream) {
        try { localVideoStream.getTracks().forEach(t => t.stop()); } catch (_) { /* noop */ }
        setLocalVideoStream(null);
      }
      toastMediaError(error);
    }
  }, [targetUserId, createWebcamPeerConnection, localVideoStream]);

  // Handle incoming webcam answer
  const handleWebcamAnswer = useCallback(async (answer) => {
    if (videoPeerConnectionRef.current) {
      await videoPeerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }, []);

  // Handle incoming ICE candidate for webcam
  const handleWebcamIceCandidate = useCallback(async (candidate) => {
    if (videoPeerConnectionRef.current) {
      await videoPeerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }, []);

  // Update webcam refs
  useEffect(() => {
    handleWebcamOfferRef.current = handleWebcamOffer;
    handleWebcamAnswerRef.current = handleWebcamAnswer;
    handleWebcamIceCandidateRef.current = handleWebcamIceCandidate;
  }, [handleWebcamOffer, handleWebcamAnswer, handleWebcamIceCandidate]);

  // Ensure remote video element gets updated when remoteVideoStream changes
  useEffect(() => {
    if (remoteVideoStream && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteVideoStream;
    }
  }, [remoteVideoStream]);

  // Ensure local video element gets updated when localVideoStream changes
  useEffect(() => {
    if (localVideoStream && localVideoRef.current) {
      localVideoRef.current.srcObject = localVideoStream;
    }
  }, [localVideoStream]);

  // Start webcam call
  const startWebcam = async () => {
    try {
      const stream = await requestMedia({ 
        video: { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true
      });
      setLocalVideoStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      const pc = createWebcamPeerConnection();
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "pm_webcam_offer",
          target_user_id: targetUserId,
          offer: offer
        }));
      }
      
      setIsWebcamEnabled(true);
      setShowWebcam(true);
      toast.success("Starting video call...");
    } catch (error) {
      console.error("Failed to start webcam:", error);
      toastMediaError(error);
    }
  };

  // Stop webcam - use a ref to prevent multiple calls
  const isStoppingWebcamRef = useRef(false);
  
  const stopWebcam = useCallback(() => {
    // Prevent multiple calls
    if (isStoppingWebcamRef.current) return;
    isStoppingWebcamRef.current = true;
    
    // Send end signal FIRST before cleanup (only once)
    if (wsRef.current?.readyState === WebSocket.OPEN && isWebcamEnabled) {
      wsRef.current.send(JSON.stringify({
        type: "pm_webcam_end",
        target_user_id: targetUserId
      }));
    }
    
    if (videoPeerConnectionRef.current) {
      videoPeerConnectionRef.current.close();
      videoPeerConnectionRef.current = null;
    }
    
    if (localVideoStream) {
      localVideoStream.getTracks().forEach(track => track.stop());
      setLocalVideoStream(null);
    }
    
    setRemoteVideoStream(null);
    setIsWebcamEnabled(false);
    setWebcamConnected(false);
    setShowWebcam(false);
    setIsTestMode(false);
    
    // Reset the stopping flag after a short delay
    setTimeout(() => {
      isStoppingWebcamRef.current = false;
    }, 100);
  }, [localVideoStream, targetUserId, isWebcamEnabled]);

  // Update stopWebcamRef after stopWebcam is defined
  useEffect(() => {
    stopWebcamRef.current = stopWebcam;
  }, [stopWebcam]);

  // Test camera - preview without calling
  const testCamera = async () => {
    if (isTestMode) {
      // Stop test mode
      if (localVideoStream) {
        localVideoStream.getTracks().forEach(track => track.stop());
        setLocalVideoStream(null);
      }
      setShowWebcam(false);
      setIsTestMode(false);
      return;
    }
    
    try {
      const stream = await requestMedia({ 
        video: { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true
      });
      setLocalVideoStream(stream);
      setShowWebcam(true);
      setIsTestMode(true);
      toast.success("Camera preview active! Click 'Start Call' to call the other person.");
    } catch (error) {
      console.error("Failed to test camera:", error);
      toastMediaError(error);
    }
  };

  // Start actual call from test mode
  const startCallFromTest = async () => {
    if (!localVideoStream) return;
    
    setIsTestMode(false);
    
    const pc = createWebcamPeerConnection();
    localVideoStream.getTracks().forEach(track => pc.addTrack(track, localVideoStream));
    
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "pm_webcam_offer",
        target_user_id: targetUserId,
        offer: offer
      }));
    }
    
    setIsWebcamEnabled(true);
    toast.success("Calling... waiting for answer");
  };

  const sendMessage = () => {
    const content = newMessage.trim();
    if (!content) return;

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // Socket is still (re)connecting — queue the message so it sends the moment we connect,
      // instead of silently dropping it.
      pendingMessagesRef.current.push(content);
      setNewMessage("");
      setShowEmoji(false);
      toast.info("Connecting… your message will send in a moment.");
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: "private_message",
      recipient_id: targetUserId,
      content,
      message_type: "text"
    }));

    setNewMessage("");
    setShowEmoji(false);
  };

  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "typing",
        recipient_id: targetUserId,
        is_typing: true
      }));

      setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: "typing",
            recipient_id: targetUserId,
            is_typing: false
          }));
        }
      }, 2000);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("authorization", `Bearer ${token}`);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "private_message",
          recipient_id: targetUserId,
          content: `Shared a file: ${response.data.file_name}`,
          message_type: "file",
          file_url: response.data.file_url,
          file_name: response.data.file_name
        }));
      }
    } catch (error) {
      toast.error("Failed to upload file");
    }

    e.target.value = "";
  };

  const handleMouseDown = (e) => {
    if (isMobile) return; // no dragging on phones (full-screen modal)
    if (e.target.closest('.retro-title-bar')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const w = Math.min(size.width, vw);
      const nx = e.clientX - dragOffset.x;
      const ny = e.clientY - dragOffset.y;
      // Clamp so the blue title bar can never be dragged off-screen.
      setPosition({
        x: Math.max(0, Math.min(nx, Math.max(0, vw - w))),
        y: Math.max(0, Math.min(ny, Math.max(0, vh - 40))),
      });
    }
    if (isResizing) {
      const newWidth = Math.max(280, resizeStartRef.current.width + (e.clientX - resizeStartRef.current.x));
      const newHeight = Math.max(300, resizeStartRef.current.height + (e.clientY - resizeStartRef.current.y));
      setSize({ width: newWidth, height: newHeight });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    };
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset]);

  // Keep the IM window inside the viewport on mount + on resize/rotation so the title bar
  // is always reachable (fixes IM boxes stuck off-screen, and phone rotation).
  useEffect(() => {
    const clampToViewport = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setSize((prev) => ({
        width: Math.min(prev.width, vw),
        height: Math.min(prev.height, Math.max(260, vh - 20)),
      }));
      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, Math.max(0, vw - Math.min(size.width, vw)))),
        y: Math.max(0, Math.min(prev.y, Math.max(0, vh - 40))),
      }));
    };
    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    window.addEventListener('orientationchange', clampToViewport);
    return () => {
      window.removeEventListener('resize', clampToViewport);
      window.removeEventListener('orientationchange', clampToViewport);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '--:--';
    }
  };

  // Render message content with custom smileys
  const renderMessageContent = (content) => {
    if (!content) return '';
    
    const smileyPattern = /\{([a-zA-Z0-9_]+)\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = smileyPattern.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      const smileyCode = match[0];
      const smileyName = match[1];
      parts.push(
        <img
          key={`smiley-${match.index}`}
          src={`${process.env.REACT_APP_BACKEND_URL}/api/smileys/${smileyName}.gif`}
          alt={smileyCode}
          title={smileyCode}
          className="inline-block h-5 align-middle mx-0.5"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.insertAdjacentText('afterend', smileyCode);
          }}
        />
      );
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : content;
  };

  return (
    <div 
      ref={windowRef}
      className={`pm-window retro-window ${isMinimized ? 'pm-minimized' : ''}`}
      style={{ 
        left: position.x, 
        top: position.y,
        width: size.width,
        height: isMinimized ? 'auto' : size.height
      }}
      onMouseDown={handleMouseDown}
      data-testid={`pm-window-${targetUserId}`}
    >
      <div className={`retro-title-bar ${isMobile ? '' : 'cursor-move'}`}>
        <span>IM - {targetUsername}</span>
        <div className="flex gap-1">
          <button 
            className="retro-button px-2 py-0 text-xs"
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
          </button>
          <button 
            className="close-button-visible"
            onClick={onClose}
            data-testid="close-pm-btn"
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="p-1 bg-[#C0C0C0] flex flex-col" style={{ height: 'calc(100% - 24px)' }}>
          {/* Voice + Video Controls */}
          <div className="flex items-center gap-1.5 mb-1 p-1.5 bg-gradient-to-b from-[#ececec] to-[#c8c8c8] border border-[#808080] flex-shrink-0">
            {!isVoiceEnabled ? (
              <button 
                className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md bg-gradient-to-b from-green-500 to-green-700 text-white shadow-md hover:from-green-400 hover:to-green-600 border border-green-800 transition-colors"
                onClick={startVoice}
                title="Start a voice call"
                data-testid="pm-start-voice-btn"
              >
                <Mic className="w-4 h-4" />
                Voice
              </button>
            ) : (
              <>
                {voiceConnected && (
                  <span className="text-[10px] text-green-600 font-bold px-1 flex items-center">
                    🟢 Connected
                  </span>
                )}
                {!voiceConnected && (
                  <span className="text-[10px] text-yellow-600 font-bold px-1 flex items-center animate-pulse">
                    📞 Calling...
                  </span>
                )}
                {isPushToTalk ? (
                  <button 
                    className={`retro-button flex items-center gap-1 text-xs px-2 ${isPTTActive ? 'bg-green-200' : 'bg-yellow-100'}`}
                    onMouseDown={handlePTTPress}
                    onMouseUp={handlePTTRelease}
                    onMouseLeave={handlePTTRelease}
                    onTouchStart={handlePTTPress}
                    onTouchEnd={handlePTTRelease}
                  >
                    <Radio className={`w-3 h-3 ${isPTTActive ? 'text-green-600' : 'text-yellow-600'}`} />
                    PTT
                  </button>
                ) : (
                  <button 
                    className={`retro-button flex items-center gap-1 text-xs px-2 ${isMuted ? 'bg-red-100' : 'bg-green-100'}`}
                    onClick={toggleMute}
                    data-testid="pm-mute-btn"
                  >
                    {isMuted ? <MicOff className="w-3 h-3 text-red-600" /> : <Mic className="w-3 h-3 text-green-600" />}
                  </button>
                )}
                <button 
                  className={`retro-button text-xs px-2 flex items-center gap-1 ${isPushToTalk ? 'bg-yellow-100' : 'bg-green-50'}`}
                  onClick={togglePushToTalk}
                  title="Switch between Push-to-Talk and Hands-Free"
                  data-testid="pm-toggle-ptt-btn"
                >
                  <Radio className={`w-3 h-3 ${isPushToTalk ? 'text-yellow-600' : 'text-gray-500'}`} />
                  {isPushToTalk ? 'PTT' : 'Hands-Free'}
                </button>
                <button 
                  className="retro-button text-xs px-2 bg-red-100"
                  onClick={stopVoice}
                  data-testid="pm-end-call-btn"
                >
                  End
                </button>
                {((!isMuted && !isPushToTalk) || isPTTActive) && voiceConnected && (
                  <div className="voice-indicator ml-1">
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                    <div className="voice-bar" />
                  </div>
                )}
              </>
            )}
            {/* Test Camera Button (secondary) */}
            <button 
              className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1.5 rounded-md border transition-colors ${isTestMode || showWebcam ? 'bg-yellow-200 border-yellow-500 text-yellow-800' : 'bg-white/70 border-[#909090] text-gray-700 hover:bg-white'}`}
              onClick={testCamera}
              title={isTestMode ? "Stop camera preview" : "Preview your camera"}
              data-testid="pm-test-camera-btn"
            >
              {isTestMode ? '👁️ Preview' : '🎥 Test'}
            </button>
            {/* Video Call Button (prominent) */}
            <button 
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md text-white shadow-md border transition-colors ${isWebcamEnabled ? 'bg-gradient-to-b from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 border-red-800' : 'bg-gradient-to-b from-blue-500 to-blue-700 hover:from-blue-400 hover:to-blue-600 border-blue-800'}`}
              onClick={() => isWebcamEnabled ? stopWebcam() : startWebcam()}
              title={isWebcamEnabled ? "End video call" : "Start a video call"}
              data-testid="pm-webcam-btn"
            >
              {isWebcamEnabled ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
              {isWebcamEnabled ? 'End Video' : 'Video'}
            </button>
            {/* Install Messenger — shown when the app isn't installed yet */}
            {!installed && (canInstall || isIos) && (
              <button
                className="flex items-center gap-1 text-[11px] font-bold px-2 py-1.5 rounded-md bg-gradient-to-b from-purple-500 to-purple-700 text-white border border-purple-800 ml-auto shadow hover:from-purple-400 hover:to-purple-600"
                onClick={async () => {
                  if (canInstall) {
                    const r = await promptInstall();
                    if (r?.outcome === "accepted") toast.success("Installing MEGA Messenger…");
                  } else {
                    setShowInstallHelp(true);
                  }
                }}
                title="Install the MEGA Messenger app"
                data-testid="pm-install-messenger-btn"
              >
                📲 Get Messenger
              </button>
            )}
          </div>

          {/* User Action Menu for PM */}
          {showUserMenu && (
            <div className="mb-1">
              <UserActionMenu
                targetUser={{ user_id: targetUserId, username: targetUsername }}
                showInline={true}
                onClose={() => setShowUserMenu(false)}
                onStartPM={() => setShowUserMenu(false)}
                onRequestWebcam={() => {
                  toast.info(`Webcam request sent to ${targetUsername}`);
                  setShowUserMenu(false);
                }}
                onViewProfile={() => {
                  window.location.href = `/profile/${targetUserId}`;
                  setShowUserMenu(false);
                }}
              />
            </div>
          )}

          {/* Messages */}
          <div className="retro-inset flex-1 overflow-y-auto retro-scrollbar p-2 mb-1 min-h-0">
            {messages.map((msg, index) => (
              <div key={msg?.message_id || `msg-${index}`} className="mb-1 text-xs">
                <span className="text-[#808080]">[{formatTime(msg?.timestamp)}]</span>
                <span className={`font-bold ml-1 ${
                  msg?.sender_id === user?.user_id ? 'text-[#7B0099]' : 'text-[#000080]'
                }`}>
                  {msg?.sender_id === user?.user_id ? user?.username : targetUsername}:
                </span>
                {msg?.message_type === "file" ? (
                  <span className="ml-1">
                    {msg?.file_url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${msg?.file_url}`} 
                        alt={msg?.file_name || 'file'} 
                        className="chat-image-thumbnail mt-1"
                        style={{ maxWidth: '150px', maxHeight: '100px' }}
                        onClick={() => setLightboxImage({
                          src: `${process.env.REACT_APP_BACKEND_URL}${msg?.file_url}`,
                          alt: msg?.file_name || 'file'
                        })}
                        title="Click to enlarge"
                      />
                    ) : (
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${msg?.file_url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#0000EE] hover:underline"
                      >
                        📎 {msg?.file_name || 'file'}
                      </a>
                    )}
                  </span>
                ) : msg?.message_type === "webcam_invite" ? (
                  <span className="ml-1 inline-flex flex-col gap-1">
                    <span>{renderMessageContent(msg?.content || '')}</span>
                    {msg?.webcam?.room_id && (
                      <button
                        className="retro-button self-start text-xs px-3 py-1 bg-gradient-to-r from-pink-200 to-purple-200 border-purple-400 font-semibold"
                        data-testid="watch-cam-btn"
                        onClick={() => {
                          const w = msg.webcam;
                          navigate(`/room/${w.room_id}?watch=${w.broadcaster_id}`);
                        }}
                      >
                        ▶ Watch
                      </button>
                    )}
                  </span>
                ) : (
                  <span className="ml-1">{renderMessageContent(msg?.content || '')}</span>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="text-xs text-[#808080] italic">
                {targetUsername} is typing
                <span className="typing-dot">.</span>
                <span className="typing-dot">.</span>
                <span className="typing-dot">.</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Toolbar */}
          <div className="flex gap-1 mb-1">
            <button 
              className="retro-button p-1"
              onClick={() => setShowEmoji(!showEmoji)}
            >
              <Smile className="w-3 h-3" />
            </button>
            <button 
              className="retro-button p-1"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="w-3 h-3" />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,video/*,.pdf,.gif"
            />
          </div>

          {showEmoji && (
            <div className="absolute z-50 bottom-full left-0">
              <EmojiPicker 
                onSelect={(emoji) => {
                  setNewMessage(prev => prev + emoji);
                  setShowEmoji(false);
                }}
                onClose={() => setShowEmoji(false)}
              />
            </div>
          )}

          {/* Input */}
          <div className="flex gap-1 flex-shrink-0">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onKeyPress={(e) => e.key === "Enter" && sendMessage()}
              className="retro-input flex-1 text-xs"
              placeholder="Type a message..."
              data-testid="pm-message-input"
            />
            <button 
              className="retro-button px-2"
              onClick={sendMessage}
              data-testid="pm-send-btn"
            >
              <Send className="w-3 h-3" />
            </button>
          </div>
          
          {/* Resize Handle (desktop only — mobile is a fixed full-screen modal) */}
          {!isMobile && (
          <div 
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
            onMouseDown={handleResizeStart}
            style={{
              background: 'linear-gradient(135deg, transparent 50%, #808080 50%, #808080 60%, transparent 60%, transparent 70%, #808080 70%, #808080 80%, transparent 80%)'
            }}
            title="Drag to resize"
          />
          )}
        </div>
      )}
      
      {/* Image Lightbox for enlarged view */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={() => setLightboxImage(null)}
        />
      )}
      
      {/* Floating Video Call Window - Opens outside PM window for better UX */}
      {showWebcam && (
        <IMVideoCallWindow
          targetUserId={targetUserId}
          targetUsername={targetUsername}
          localStream={localVideoStream}
          remoteStream={remoteVideoStream}
          isTestMode={isTestMode}
          webcamConnected={webcamConnected}
          onStartCall={startCallFromTest}
          onEndCall={stopWebcam}
          onClose={() => {
            if (isTestMode) {
              testCamera(); // Stop test mode
            } else {
              stopWebcam();
            }
          }}
        />
      )}
      {/* iOS "Add to Home Screen" help for installing the Messenger */}
      {showInstallHelp && (
        <div className="fixed inset-0 z-[10050] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowInstallHelp(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full p-5 text-center" onClick={e => e.stopPropagation()} data-testid="pm-ios-install-help">
            <img src="/messenger-logo.png" alt="Messenger" className="w-20 h-20 mx-auto object-contain" />
            <h3 className="font-black text-purple-900 mt-2">Install MEGA Messenger</h3>
            <p className="text-sm text-zinc-600 mt-2">
              On iPhone/iPad: tap the <b>Share</b> button in Safari, then choose <b>“Add to Home Screen”</b> to install the Messenger app.
            </p>
            <button onClick={() => setShowInstallHelp(false)} className="mt-4 w-full py-2 rounded-full bg-purple-700 text-white font-bold text-sm">Got it</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateMessageWindow;
